/**
 * Utility functions for managing logo dynamically
 */

import { getSettings } from '@/helpers/settings-cache';

// Default logo paths - webpack will handle these requires
const getDefaultLogo = (type) => {
  try {
    switch (type) {
      case 'light':
        return require('@/assets/logo-light.png');
      case 'sm':
        return require('@/assets/logo-dark-sm.png');
      default:
        return require('@/assets/logo-dark.png');
    }
  } catch (e) {
    // Fallback paths if require doesn't work
    switch (type) {
      case 'light':
        return '/img/logo-light.png';
      case 'sm':
        return '/img/logo-dark-sm.png';
      default:
        return '/img/logo-dark.png';
    }
  }
};

/**
 * Get logo URL from settings or return default
 * @param {string} type - 'dark', 'light', or 'sm'
 * @returns {string} Logo URL
 */
export function getLogoUrl(type = 'dark') {
  const logoUrl = localStorage.getItem('appLogo');
  if (logoUrl) {
    return logoUrl;
  }
  
  // Return default based on type
  return getDefaultLogo(type);
}

/**
 * Get logo dimensions from settings
 * @returns {Object} { width, height }
 */
export function getLogoDimensions() {
  const width = localStorage.getItem('logoWidth');
  const height = localStorage.getItem('logoHeight');
  return {
    width: width || null,
    height: height || null
  };
}

/**
 * Fetch and store logo settings (uses cached settings to avoid duplicate API calls)
 */
export async function loadLogoSettings() {
  try {
    const res = await getSettings();
    if (res?.data?.logo && typeof res.data.logo === 'string') {
      localStorage.setItem('appLogo', res.data.logo);
    }
    if (res?.data?.logo_width) {
      localStorage.setItem('logoWidth', res.data.logo_width);
    }
    if (res?.data?.logo_height) {
      localStorage.setItem('logoHeight', res.data.logo_height);
    }
  } catch (error) {
    console.log('Settings not available for logo');
  }
}

/**
 * Update logo in localStorage (called when settings are saved)
 * @param {string} logoUrl - Logo URL
 * @param {string} width - Logo width
 * @param {string} height - Logo height
 */
export function updateLogoSettings(logoUrl, width, height) {
  if (logoUrl) {
    localStorage.setItem('appLogo', logoUrl);
  }
  if (width) {
    localStorage.setItem('logoWidth', width);
  }
  if (height) {
    localStorage.setItem('logoHeight', height);
  }
  
  // Dispatch custom event to notify components
  window.dispatchEvent(new CustomEvent('logo-updated', { 
    detail: { logoUrl, width, height } 
  }));
}

